import { GRPC_FETCH_TIMEOUT } from './constants';
import { getErrorMessage } from './getErrorMessage';
import { handleAndThrowForBotTraffic } from './handleAndThrowForBotTraffic';
import { handleAndThrowForTimeouts } from './handleAndThrowForTimeouts';
import { logGrpcClientWarn } from './logGrpcClientWarn';
import { mapGrpcToHttpStatusCode } from './mapGrpcErrorCodes';
import {
  shimIntoNetworkResponseError,
  shimIntoNetworkResponseSuccess,
} from './shimIntoNetworkResponse';

import type { InitReq, Middleware } from '@skyscanner-internal/web-http-client-fetch/fetch.pb';

const GRPC_FETCH_MAX_RETRIES = 3;
export const GRPC_FETCH_RETRY_DELAY = 300;
const grpcErrorCodesToRetry = [
  4, // DEADLINE_EXCEEDED
  8, // RESOURCE_EXHAUSTED
  14, // UNAVAILABLE
];

const delayPromise = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

// Supports both pre and post web-http v8 error shapes - can clean up when all clients using new format
const extractGrpcErrorDetails = (error: unknown): any => {
  if (error instanceof Error && error.cause && typeof error.cause === 'object') {
    return error.cause;
  }
  return error;
};

const requestTimeout = (timeoutMs: number) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  return {
    clearRequestTimeout: () => clearTimeout(id),
    signal: controller.signal,
  };
};

type ShimmedResponse<Res extends Record<string, unknown>> =
  | ReturnType<typeof shimIntoNetworkResponseSuccess<Res>>
  | ReturnType<typeof shimIntoNetworkResponseError>;

export type GrpcRequestMethod<
  Req extends Record<string, unknown>,
  Res extends Record<string, unknown>,
> = (request: Req, initRequest: InitReq) => Promise<Res>;

export type GrpcClientWrapperOptions = {
  clientName: string;
  pathPrefix: string;
  betaInterceptors?: Middleware[];
  timeoutMs?: number;
};

const makeRequest = async <
  Req extends Record<string, unknown>,
  Res extends Record<string, unknown>,
>({
  betaInterceptors,
  body,
  clientName,
  headers,
  pathPrefix,
  requestCount = 1,
  requestMethod,
  timeoutMs = GRPC_FETCH_TIMEOUT,
}: {
  betaInterceptors?: Middleware[];
  body: Req;
  clientName: string;
  headers: InitReq['headers'];
  pathPrefix: string;
  requestCount?: number;
  requestMethod: GrpcRequestMethod<Req, Res>;
  timeoutMs?: number;
}): Promise<ShimmedResponse<Res>> => {
  const { clearRequestTimeout, signal } = requestTimeout(timeoutMs);

  try {
    const response = await requestMethod(body, {
      headers,
      pathPrefix,
      signal,
      betaInterceptors,
    });

    // return a success response in a network layer compatible shape
    return shimIntoNetworkResponseSuccess(response);
  } catch (error: any) {
    const errorDetails = extractGrpcErrorDetails(error);

    handleAndThrowForBotTraffic(errorDetails);

    handleAndThrowForTimeouts(error);

    const httpStatusCode = mapGrpcToHttpStatusCode(errorDetails.code);

    if (grpcErrorCodesToRetry.includes(errorDetails.code)) {
      if (requestCount <= GRPC_FETCH_MAX_RETRIES) {
        logGrpcClientWarn({
          clientName,
          error: errorDetails,
          message: `Error making request, retrying - attempt ${requestCount} of ${GRPC_FETCH_MAX_RETRIES}`,
          body,
          headers,
          httpStatusCode,
        });

        await delayPromise(GRPC_FETCH_RETRY_DELAY);
        // recursively retry request until GRPC_FETCH_MAX_RETRIES reached
        return makeRequest({
          requestMethod,
          body,
          headers,
          pathPrefix,
          clientName,
          betaInterceptors,
          requestCount: requestCount + 1,
          timeoutMs,
        });
      }

      // log for max retries exceeded
      logGrpcClientWarn({
        clientName,
        error: errorDetails,
        message: `Error making request. Max retries (${GRPC_FETCH_MAX_RETRIES}) exceeded. Error Code: ${errorDetails.code}`,
        body,
        headers,
        httpStatusCode,
      });
    } else if (httpStatusCode) {
      // log for grpc error code that isn't retryable
      logGrpcClientWarn({
        clientName,
        error: errorDetails,
        message: `Error making request. Non-retryable error code: ${errorDetails.code}`,
        body,
        headers,
        httpStatusCode,
      });
    }

    if (!httpStatusCode) {
      logGrpcClientWarn({
        clientName,
        error: errorDetails,
        message: `Status code undefined. Error: ${getErrorMessage(error)}`,
        body,
        headers,
      });
      throw error;
    }

    // return an error response in a network layer compatible shape
    return shimIntoNetworkResponseError(
      {
        message: errorDetails.message,
      },
      httpStatusCode,
    );
  } finally {
    clearRequestTimeout();
  }
};

export const grpcFetchClientWrapper =
  <Req extends Record<string, unknown>, Res extends Record<string, unknown>>(
    requestMethod: GrpcRequestMethod<Req, Res>,
    { betaInterceptors, clientName, pathPrefix, timeoutMs }: GrpcClientWrapperOptions,
  ) =>
  async ({ body, headers }: { body?: Req; headers: InitReq['headers'] }) => {
    if (!body) {
      const message = 'request error - request body undefined';
      const error = new Error(`${clientName} ${message}`);

      logGrpcClientWarn({
        clientName,
        message,
        body,
        headers,
        error,
      });

      throw error;
    }

    return makeRequest({
      requestMethod,
      body,
      headers,
      pathPrefix,
      clientName,
      betaInterceptors,
      timeoutMs,
    });
  };

const makeRequestNoHeaders = async <
  Req extends Record<string, unknown>,
  Res extends Record<string, unknown>,
>({
  body,
  clientName,
  pathPrefix,
  requestCount = 1,
  requestMethod,
  timeoutMs = GRPC_FETCH_TIMEOUT,
}: {
  body: Req;
  clientName: string;
  pathPrefix: string;
  requestCount?: number;
  requestMethod: GrpcRequestMethod<Req, Res>;
  timeoutMs?: number;
}): Promise<ShimmedResponse<Res>> => {
  const { clearRequestTimeout, signal } = requestTimeout(timeoutMs);

  try {
    const response = await requestMethod(body, {
      pathPrefix,
      signal,
    });

    // return a success response in a network layer compatible shape
    return shimIntoNetworkResponseSuccess(response);
  } catch (error: any) {
    const errorDetails = extractGrpcErrorDetails(error);

    handleAndThrowForBotTraffic(errorDetails);

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }

    const httpStatusCode = mapGrpcToHttpStatusCode(errorDetails.code);

    if (grpcErrorCodesToRetry.includes(errorDetails.code)) {
      logGrpcClientWarn({
        clientName,
        error: errorDetails,
        message: `Error making request, attempt ${requestCount} of ${GRPC_FETCH_MAX_RETRIES}`,
        body,
        headers: {},
        httpStatusCode,
        component: 'grpcFetchClientWrapperNoHeaders',
      });

      if (requestCount < GRPC_FETCH_MAX_RETRIES) {
        await delayPromise(GRPC_FETCH_RETRY_DELAY);
        return makeRequestNoHeaders({
          requestMethod,
          body,
          pathPrefix,
          clientName,
          requestCount: requestCount + 1,
          timeoutMs,
        });
      }
    }

    logGrpcClientWarn({
      clientName,
      error: errorDetails,
      message: `Error making request, will not retry (attempt ${requestCount} of ${GRPC_FETCH_MAX_RETRIES}). Error Code: ${errorDetails.code}`,
      body,
      headers: {},
      httpStatusCode,
      component: 'grpcFetchClientWrapperNoHeaders',
    });

    if (httpStatusCode) {
      return shimIntoNetworkResponseError({ message: errorDetails.message }, httpStatusCode);
    }

    throw error;
  } finally {
    clearRequestTimeout();
  }
};

export const grpcFetchClientWrapperNoHeaders =
  <Req extends Record<string, unknown>, Res extends Record<string, unknown>>(
    requestMethod: GrpcRequestMethod<Req, Res>,
    { clientName, pathPrefix, timeoutMs }: Omit<GrpcClientWrapperOptions, 'betaInterceptors'>,
  ) =>
  async ({ body }: { body?: Req }) => {
    if (!body) {
      const message = 'request error - request body undefined';
      const error = new Error(`${clientName} ${message}`);

      logGrpcClientWarn({
        clientName,
        message,
        body,
        headers: {},
        error,
        component: 'grpcFetchClientWrapperNoHeaders',
      });

      throw error;
    }

    return makeRequestNoHeaders({ requestMethod, body, pathPrefix, clientName, timeoutMs });
  };
